
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Post } from "@/hooks/posts/types";

interface EditPostModalProps {
  post: Post | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedPost: Post) => void;
}

export const EditPostModal = ({ post, isOpen, onClose, onSave }: EditPostModalProps) => {
  const [content, setContent] = useState(post?.content || '');
  const [crop, setCrop] = useState(post?.crop || '');
  const [sentiment, setSentiment] = useState(post?.sentiment || 'standard');
  const [whatsHappening, setWhatsHappening] = useState(post?.whatsHappening || '');
  const [challenges, setChallenges] = useState(post?.challenges || '');
  const [harvestWeekMonth, setHarvestWeekMonth] = useState(post?.harvestWeekMonth || '');

  const handleSave = () => {
    if (!post) return;
    
    const updatedPost: Post = {
      ...post,
      content,
      crop,
      sentiment,
      whatsHappening,
      challenges,
      harvestWeekMonth
    };
    
    onSave(updatedPost);
    onClose();
  };

  const handleClose = () => {
    // Reset form when closing
    setContent(post?.content || '');
    setCrop(post?.crop || '');
    setSentiment(post?.sentiment || 'standard');
    setWhatsHappening(post?.whatsHappening || '');
    setChallenges(post?.challenges || '');
    setHarvestWeekMonth(post?.harvestWeekMonth || '');
    onClose();
  };

  if (!post) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Post</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Content</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share what's happening on your farm..."
              className="min-h-[100px]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Crop</label>
            <Input
              value={crop}
              onChange={(e) => setCrop(e.target.value)}
              placeholder="e.g., Tomatoes, Corn, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Sentiment</label>
            <Select value={sentiment} onValueChange={setSentiment}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="great">🎉 Great</SelectItem>
                <SelectItem value="standard">📈 Standard</SelectItem>
                <SelectItem value="help">🆘 Help</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">What's Happening</label>
            <Textarea
              value={whatsHappening}
              onChange={(e) => setWhatsHappening(e.target.value)}
              placeholder="Describe what's currently happening..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Challenges</label>
            <Textarea
              value={challenges}
              onChange={(e) => setChallenges(e.target.value)}
              placeholder="Any challenges you're facing..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Harvest Period</label>
            <Input
              value={harvestWeekMonth}
              onChange={(e) => setHarvestWeekMonth(e.target.value)}
              placeholder="e.g., Week 3 of August 2024"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
